import React from 'react';
import { ArrowLeft } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { Status } from '../../types';
import { TICKET_STATUSES } from '../../constants/ticketConstants';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuthContext } from '../../contexts/AuthContext';
import { Ticket } from '../../types/ticket';
import { useTranslation } from 'react-i18next';

interface TicketHeaderProps {
  ticket: Ticket;
  onStatusChange: (status: Status) => void;
}

export default function TicketHeader({ 
  ticket, 
  onStatusChange,
}: TicketHeaderProps) {
  const location = useLocation();
  const { theme } = useTheme();
  const { t } = useTranslation();
  const fromPage = location.state?.page || 1;
  const fromPath = location.state?.from || '/tickets';

  const { user } = useAuthContext();
  
  const canEdit = user?.role === 'admin' || 
    (user?.role === 'agent') || 
    (user?.role === 'requester' && ticket?.created_by?.id === user?.id);

  return (
    <div className={`border-b pb-4 ${
      theme === 'dark' ? 'border-gray-700' : 'border-gray-200'
    }`}>
      <div className="flex items-center mb-4">
        <Link
          to={fromPath}
          state={{ page: fromPage }}
          className={`flex items-center text-sm ${
            theme === 'dark'
              ? 'text-gray-400 hover:text-gray-300'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          <ArrowLeft className="h-4 w-4 mr-1" />
          {t('navigation.backTo')} {
            fromPath === '/reports' 
              ? t('navigation.reports') 
              : fromPath === '/dashboard'
                ? t('navigation.dashboard')
                : t('navigation.tickets')
          }
        </Link>
      </div>
      
      <div className="flex justify-between items-center">
        <div>
          <h1 className={`text-2xl font-bold ${
            theme === 'dark' ? 'text-gray-100' : 'text-gray-900'
          }`}>
            {ticket.title}
          </h1>
          <p className={`text-sm ${
            theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
          }`}>
            {t('tickets.ticketNumber', 'Ticket #')}{ticket.id}
          </p>
        </div>
        
        {canEdit && (
          <select
            value={ticket.status}
            onChange={(e) => onStatusChange(e.target.value as Status)}
            className={`ml-4 rounded-md py-2 pl-3 pr-10 text-base sm:text-sm focus:outline-none focus:ring-2 ${
              theme === 'dark'
                ? 'bg-gray-700 border-gray-600 text-gray-200 focus:border-blue-500 focus:ring-blue-500'
                : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500'
            }`}
          >
            {TICKET_STATUSES.map(({ value, label }) => (
              <option 
                key={value} 
                value={value}
                className={theme === 'dark' ? 'bg-gray-700' : 'bg-white'}
              >
                {label}
              </option>
            ))}
          </select>
        )}
      </div>
    </div>
  );
}